import {
  useContext,
  useState,
  useEffect,
  useRef,
  memo,
  useCallback,
  useMemo,
} from "react";

//Components
import HamburgerMenu from "./HamburgerMenu";
import Preferences from "./Preferences";
import ChangePassword from "./ChangePassword/ChangePassword";
import {
  MenuItem,
  Box,
  Typography,
  Button,
  Tooltip,
  TextField,
  FormControl,
  Select,
  FormHelperText,
  IconButton,
  Chip,
  AppBar,
} from "@material-ui/core";
import {
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon,
  Room,
  Brightness4,
} from "@material-ui/icons";
import { Link } from "react-router-dom";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DmvQueryModal from "./DMVQueryModal/DmvQueryModal";
import RadioLogModal from "./RadioLogModal";
import { GiWalkieTalkie } from "react-icons/gi";
import { FaGraduationCap } from "react-icons/fa";
import { MdPublic } from "react-icons/md";
import { FaIdCard as PersonIcon } from "react-icons/fa";
import { DirectionsCar as DirectionsCarIcon } from "@material-ui/icons";
import { AiFillMessage } from "react-icons/ai";
import NewsModal from "./NewsModal";

//Contexts
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { UserContext } from "../Contexts/UserContext";
import { DashboardContext } from "../Contexts/DashboardContext";
import { AVAILABLE_OFFICER_ACTIONS } from "../Actions/AvailableOfficerActions";
import { SetupContext } from "../Contexts/SetupContext";
import { MutationContext } from "../Contexts/MutationContext";

//Query and mutations
import { useMutation, useLazyQuery } from "@apollo/client";
import { GET_ORIS_QUERY } from "../Queries/GetOrisQuery";
import { ACTIVE_INCIDENTS_ACTIONS } from "../Actions/ActiveIncidentsActions";
import { NEW_INCIDENT_MUTATION } from "../Mutations/NewIncidentMutation";
import { ADD_OR_UPDATE_OFFICER_IN_INCIDENT } from "../Mutations/AddOrUpdateOfficerInIncident";
import { UPDATE_GEOLOCATION } from "../Mutations/UpdateGeolocation";

//Styling
import { useStyles, darkTheme } from "../Styles/styles";
import clsx from "clsx";
import { ThemeProvider, withStyles } from "@material-ui/styles";

//Utils
import { createEmptyIncident } from "../utils/EmptyObjects";
import {
  checkLocalStorageAge,
  localGetData,
  localStoreData,
  parseObjectArrayIdString,
  sortByOriName,
} from "../utils/utils";
import { checkRoles } from "../utils/Roles";
import _ from "lodash";
import { MdSwapCalls } from "react-icons/md";
import { GET_TD_SERVER_URL } from "../Queries/GetTdServerUrl";
import { GET_PORTAL_SERVER_URL } from "../Queries/GetPortalServerUrl";
import { throttleFunction } from "../utils/throttleFunction";
import useUpdateOpenArray from "../CustomHooks/useUpdateOpenArray";

import moment from "moment";

const showPortalLink = window.location.hostname.includes("localhost");
// ||window.location.hostname.includes("testing");

const DashboardNavBar = ({
  openChat,
  openCore,
  handleIncidentOpening,
  getCallInfo,
  handlePersonOpening,
  handleTicketOpening,
  handleVehicleOpening,
  setPopper,
  radioLogIncidents,
  tab,
  setTab,
  setPausePolling,
  userPreferences,
  setUserPreferences,
  dashboardNavBarRef,
  createNewIncident,
  subscriptionServer,
  socketConnected = true,
}) => {
  const classes = useStyles();
  const {
    user,
    setUser,
    responseErrorHandler,
    nightMode,
    toggleNightMode,
    onlineState,
  } = useContext(UserContext);
  const {
    activeIncidents,
    activeIncidentDispatch,
    availableOfficerDispatch,
    setAvailOfficer,
  } = useContext(DashboardContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { saOris, setup } = useContext(SetupContext);
  const {
    updateCallType,
    updateCallTeam,
    setOfficerIncidentTimes,
    setStatus,
    updateIncidentReview,
    updateIncidentDetail,
  } = useContext(MutationContext);
  const {
    ADD_NEW_INCIDENT,
    REPLACE_ACTIVE_INCIDENT,
    UPDATE_ACTIVE_INCIDENT_PROPERTY,
    UPDATE_DEPT_ATTRB,
  } = ACTIVE_INCIDENTS_ACTIONS;
  const { ADD_OFFLINE_INCIDENT, REPLACE_INCIDENT, UPDATE_INCIDENT_DETAIL } =
    INCIDENT_ACTIONS;
  const { UPDATE_AVAILABLE_OFFICER } = AVAILABLE_OFFICER_ACTIONS;
  const localStoreSpecificSetup =
    localGetData(`specific-setup-${user?.homeOriId}`) || {};
  const { setupLists: specificSetupLists } = localStoreSpecificSetup || {};
  const { classifications, incidentOrigins } = specificSetupLists || {};
  const { logCodes, reviewStatuses } = setup.setupLists || {};
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [showRadioModal, setShowRadioModal] = useState(false);
  const [queryType, setQueryType] = useState(null);
  const [oriList, setOriList] = useState([]);

  const handleUpdateOpenIncident = useUpdateOpenArray(user);
  const [updateGeolocation] = useMutation(UPDATE_GEOLOCATION);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [carStopTime, setCarStopTime] = useState(null);

  const [getOris, { loading: orisLoading }] = useLazyQuery(GET_ORIS_QUERY, {
    fetchPolicy: "no-cache",
    context: { serializationKey: "incidentQueue" },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        setOriList(sortByOriName(data.getOris));
        localStoreData(data, `mp-oris`);
      }
    },
  });

  const tdWindowRef = useRef(null);
  const portalWindowRef = useRef(null);
  const [getTdServerUrl] = useLazyQuery(GET_TD_SERVER_URL, {
    fetchPolicy: "no-cache",
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (
        data &&
        data.getTdServerUrl &&
        data.getTdServerUrl.url &&
        data.getTdServerUrl.args
      ) {
        const url =
          data.getTdServerUrl.url + "/?td_arg=" + data.getTdServerUrl.args;
        tdWindowRef.current = window.open(url, "tdWindow");
      }
    },
  });

  const [getPortalServerUrl] = useLazyQuery(GET_PORTAL_SERVER_URL, {
    fetchPolicy: "no-cache",
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (
        data &&
        data.getPortalServerUrl &&
        data.getPortalServerUrl.url &&
        data.getPortalServerUrl.args
      ) {
        const url =
          data.getPortalServerUrl.url +
          "/link_to?dm_arg=" +
          data.getPortalServerUrl.args;
        portalWindowRef.current = window.open(url, "portalWindow");
      }
    },
  });

  const handleOpenPortal = () => {
    if (portalWindowRef.current && !portalWindowRef.current.closed) {
      window.open("", "portalWindow");
    } else {
      getPortalServerUrl({
        variables: { originatorId: user?.id, officerId: user?.id },
      });
    }
  };

  const handleOpenTrainingDashboard = () => {
    if (tdWindowRef.current && !tdWindowRef.current.closed) {
      window.open("", "tdWindow");
    } else {
      getTdServerUrl({
        variables: { originatorId: user?.id, officerId: user?.id },
      });
    }
  };

  useEffect(() => {
    if (checkLocalStorageAge(`mp-oris`, 8)) {
      setOriList(sortByOriName(localGetData(`mp-oris`)?.getOris));
    } else {
      !orisLoading && getOris();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event, value) => {
    localStoreData(value, `${user?.id}-selectedOris`);
    setUser(prevState => {
      return { ...prevState, selectedOrisObjs: value };
    });
  };

  const selectedOriOptions = useMemo(
    () =>
      user?.selectedOrisObjs
        ? oriList.filter(option =>
            user.selectedOrisObjs.some(selected => option.id === selected.id),
          )
        : [],
    [oriList, user?.selectedOrisObjs],
  );

  const isOnIncident = useCallback(
    id => {
      let result = false;
      (activeIncidents || []).forEach(ai => {
        ai?.officerIncidentSpecifics?.forEach(ois => {
          if (
            ois?.officer?.id?.toString() === id.toString() &&
            !ois?.clearedAt
          ) {
            result = true;
          }
        });
      });
      return result;
    },
    [activeIncidents],
  );

  const handleDutyChange = useCallback(
    e => {
      const tenCode = e.target.value ? "41" : "42";
      const logCode = logCodes?.find(
        logCode => logCode?.name?.split("/")?.[0] === tenCode,
      );
      const onIncident = isOnIncident(user?.id);
      if (onIncident && tenCode === "42") {
        alert(
          "You are currently assigned to an active incident. Please clear yourself and try again.",
        );
        return null;
      } else {
        let localOfficer = _.cloneDeep(user?.officer);
        localOfficer.deptAttribute.onDuty = e.target.value;
        setUser(prevState => {
          return {
            ...prevState,
            onDuty: e.target.value,
            officer: localOfficer,
          };
        });
        logCode?.id &&
          setStatus({
            variables: {
              input: {
                originatorId: user?.id,
                officerId: user?.id,
                logCodeId: logCode?.id,
                statusCode: tenCode,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        if (tenCode === "42") {
          updateGeolocation({
            variables: {
              input: {
                originatorId: user?.id,
                officerId: user?.id,
                lat: null,
                lng: null,
                accuracy: null,
                noSessionReset: true,
              },
            },
          });
          setAvailOfficer({
            variables: {
              input: {
                officerId: user?.id,
                originatorId: user?.id,
                secondaryAddress: "",
              },
            },
          });
          availableOfficerDispatch({
            type: UPDATE_AVAILABLE_OFFICER,
            payload: {
              officerId: user?.id,
              property: "officerLocation",
              value: "",
            },
          });
          availableOfficerDispatch({
            type: UPDATE_AVAILABLE_OFFICER,
            payload: {
              officerId: user?.id,
              property: "status",
              value: tenCode,
            },
          });
          // let newOfficers = availableOfficers.filter(o => o?.id.toString() !== user?.id.toString())
          // availableOfficerDispatch({ type: SET_STATE, payload: newOfficers })
        }
        if (tenCode === "41") {
          // let newOfficers = _.cloneDeep(availableOfficers)
          availableOfficerDispatch({
            type: UPDATE_AVAILABLE_OFFICER,
            payload: {
              officerId: user?.id,
              property: "status",
              value: tenCode,
            },
          });
          // newOfficers.unshift(user?.officer)
          // availableOfficerDispatch({ type: SET_STATE, payload: newOfficers })
        }
      }
    },
    [
      UPDATE_AVAILABLE_OFFICER,
      availableOfficerDispatch,
      isOnIncident,
      logCodes,
      setAvailOfficer,
      setStatus,
      setUser,
      updateGeolocation,
      user?.id,
      user?.officer,
    ],
  );

  const [addOrUpdateOfficerInIncident] = useMutation(
    ADD_OR_UPDATE_OFFICER_IN_INCIDENT,
    {
      onCompleted: data => {
        if (data) {
          responseErrorHandler(data);
          const payload = data[Object.keys(data)[0]];
          if (!payload?.officerIncidentSpecific || !payload?.incidentUuid) {
            return;
          }
          const res = {
            ...payload,
            officerIncidentSpecific: {
              ...payload.officerIncidentSpecific,
            },
          };
          res.officerIncidentSpecific.enrouteAt = carStopTime;
          res.officerIncidentSpecific.dispatchedAt = carStopTime;
          res.officerIncidentSpecific.onSceneAt = carStopTime;
          const onSceneLogCode = logCodes?.find(
            lc => lc.name === "23/On Scene",
          );
          const slashIndex = onSceneLogCode?.name?.indexOf("/");
          const tenCode = onSceneLogCode?.name?.substring(0, slashIndex);
          if (onSceneLogCode) {
            res.officerIncidentSpecific.logCodeId = onSceneLogCode.id;
            res.officerIncidentSpecific.logCode = onSceneLogCode;
          }

          setOfficerIncidentTimes({
            variables: {
              input: {
                originatorId: user?.id,
                officerIncidentSpecificId: res.officerIncidentSpecific?.id,
                dispatchedAt: carStopTime,
                enrouteAt: carStopTime,
                onSceneAt: carStopTime,
              },
            },
            context: { serializationKey: "incidentQueue" },
          }).then(() => {
            getCallInfo({ variables: { incidentId: res.incidentUuid } });
          });
          activeIncidentDispatch({
            type: UPDATE_ACTIVE_INCIDENT_PROPERTY,
            payload: {
              incidentId: res.incidentUuid,
              property: "officerIncidentSpecifics",
              value: [res.officerIncidentSpecific],
            },
          });
          activeIncidentDispatch({
            type: UPDATE_DEPT_ATTRB,
            payload: {
              incidentId: res.incidentUuid,
              oisId: res.officerIncidentSpecific.id,
              property: "status",
              value: tenCode,
            },
          });
          setCarStopTime(null);
        }
      },
    },
  );

  const [createNewCarStop] = useMutation(NEW_INCIDENT_MUTATION, {
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const incident = data[Object.keys(data)[0]].incident;
        const classificationId =
          classifications?.find(c => c.name === "Traffic Stop")?.id ||
          classifications?.find(c => c.name === "Traffic")?.id;
        const onSceneLogCode = logCodes?.find(lc => lc.name === "23/On Scene");
        let dispatchedCode = logCodes?.find(lc => lc.name.startsWith("D/"));
        let enrouteCode = logCodes?.find(lc => lc.name.startsWith("76/"));
        const localDate = moment().utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";
        const carStopEntryText = carStopTime
          ? moment
              .utc(carStopTime, "YYYY-MM-DD HH:mm:ss UTC")
              .local()
              .format("YYYY-MM-DD HH:mm:ss")
          : "";
        const tenCode = onSceneLogCode?.name?.substring(
          0,
          onSceneLogCode?.name?.indexOf("/"),
        );
        activeIncidentDispatch({ type: ADD_NEW_INCIDENT, payload: incident });
        handleIncidentOpening(incident.id, true);
        availableOfficerDispatch({
          type: UPDATE_AVAILABLE_OFFICER,
          payload: {
            officerId: user?.id,
            property: "lastContactedAt",
            value: localDate,
          },
        });
        availableOfficerDispatch({
          type: UPDATE_AVAILABLE_OFFICER,
          payload: { officerId: user?.id, property: "status", value: tenCode },
        });
        incidentDispatch({
          type: UPDATE_INCIDENT_DETAIL,
          payload: {
            incidentId: incident?.id,
            property: "occurredFrom",
            value: moment
              .utc(incident?.callTime, "YYYY-MM-DD HH:mm:ss UTC")
              .local(),
          },
        });
        incidentDispatch({
          type: UPDATE_INCIDENT_DETAIL,
          payload: {
            incidentId: incident?.id,
            property: "occurredTo",
            value: moment
              .utc(incident?.callTime, "YYYY-MM-DD HH:mm:ss UTC")
              .local(),
          },
        });

        updateCallType({
          variables: {
            input: {
              incidentId: incident?.id,
              originatorId: user?.id,
              classificationId: classificationId,
              incidentOriginId: incidentOrigins?.find(
                io => io.name === "Officer/Radio",
              )?.id,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        if (user?.team?.id) {
          updateCallTeam({
            variables: {
              input: {
                incidentId: incident?.id,
                originatorId: user?.id,
                teamId: user?.team?.id,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        }
        addOrUpdateOfficerInIncident({
          variables: {
            input: {
              incidentId: incident.id,
              originatorId: user?.id,
              officerIncidentSpecificId:
                incident.officerIncidentSpecifics[0].id,
              officerId: user?.id,
              primaryOfficer: true,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        setStatus({
          variables: {
              input: {
                originatorId: user?.id,
                officerId: user?.id,
                officerIncidentSpecificId:
                  incident?.officerIncidentSpecifics[0].id,
                logCodeId: dispatchedCode?.id,
                statusCode: dispatchedCode?.name?.split("/")?.[0],
                entryText: carStopEntryText,
              },
            },
          context: { serializationKey: "incidentQueue" },
        });
        setStatus({
          variables: {
              input: {
                originatorId: user?.id,
                officerId: user?.id,
                officerIncidentSpecificId:
                  incident?.officerIncidentSpecifics[0].id,
                logCodeId: enrouteCode?.id,
                statusCode: enrouteCode?.name?.split("/")?.[0],
                entryText: carStopEntryText,
              },
            },
          context: { serializationKey: "incidentQueue" },
        });

        setStatus({
          variables: {
              input: {
                originatorId: user?.id,
                officerId: user?.id,
                officerIncidentSpecificId:
                  incident?.officerIncidentSpecifics[0].id,
                logCodeId: onSceneLogCode?.id,
                statusCode: onSceneLogCode?.name?.split("/")?.[0],
                entryText: carStopEntryText,
              },
            },
          context: { serializationKey: "incidentQueue" },
        }).then(res => {
          const resTenCode =
            res?.data?.setStatus?.officer?.deptAttribute?.status;
          const officerId = res?.data?.setStatus?.officer?.id;
          if (officerId != null && resTenCode != null) {
            availableOfficerDispatch({
              type: UPDATE_AVAILABLE_OFFICER,
              payload: {
                officerId: officerId,
                property: "status",
                value: resTenCode,
              },
            });
          }
        });
        const pendingStatusId = reviewStatuses
          ?.find(r => r.name === "Pending")
          ?.id.toString();
        updateIncidentReview({
          variables: {
            input: {
              incidentReviewId: incident?.incidentReview?.id,
              originatorId: user?.id,
              incidentId: incident?.id,
              investigatingOfficerId: user?.id,
              reviewStatusId: pendingStatusId,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });

        updateIncidentDetail({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incident?.id,
              incidentDetailId: incident?.incidentDetail?.id,
              investigatingOfficerId: user?.id,
              occurredFrom: incident?.callTime,
              occurredTo: incident?.callTime,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      }
    },
  });

  const throttledCreateNewCarStop = throttleFunction(createNewCarStop);

  const [createIncidentThenReplace] = useMutation(NEW_INCIDENT_MUTATION, {
    onCompleted: data => {
      if (data) {
        const incident = data[Object.keys(data)[0]]?.incident;
        if (incident) {
          incidentDispatch({ type: REPLACE_INCIDENT, payload: incident });
          activeIncidentDispatch({
            type: REPLACE_ACTIVE_INCIDENT,
            payload: incident,
          });
        }
      }
    },
  });

  const throttledCreateIncidentThenReplace = throttleFunction(
    createIncidentThenReplace,
  );

  const newIncident = carStop => {
    //need a logCode
    if (!user?.onDuty) {
      if (
        !window.confirm(
          "You are not on duty. Would you like to be placed on duty and continue?",
        )
      ) {
        return null;
      } else {
        handleDutyChange({ target: { value: true } });
      }
    }
    const now = moment().utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";
    carStop && setCarStopTime(now);
    const incidentPayload = createEmptyIncident(user, carStop, saOris);
    const input = {
      officerId: user?.id,
      oriId: user?.homeOriId,
      uuid: incidentPayload.id,
      callTime: incidentPayload.callTime,
    };
    //Mutation to create the incident
    if (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true") {
      if (carStop) {
        //Check officer on duty status - if off duty, set on duty.
        throttledCreateNewCarStop({
          variables: { input },
          context: { serializationKey: "incidentQueue" },
        });
      } else {
        createNewIncident({
          variables: { input },
          context: { serializationKey: "incidentQueue" },
        });
      }
    } else {
      throttledCreateIncidentThenReplace({
        variables: { input },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      handleUpdateOpenIncident(incidentPayload.id, "open", "incidents");
      updateCallType({
        variables: {
          input: {
            incidentId: incidentPayload?.id,
            originatorId: user?.id,
            classificationId: incidentPayload?.classification?.id,
            incidentOriginId: incidentPayload?.incidentOrigin?.id,
          },
        },
        context: { serializationKey: "incidentQueue" },
      });
      if (carStop) {
        const logCodeId = logCodes?.find(lc => lc.name === "23/On Scene")?.id;
        addOrUpdateOfficerInIncident({
          variables: {
            input: {
              incidentId: incidentPayload?.id,
              originatorId: user?.id,
              officerIncidentSpecificId:
                incidentPayload?.officerIncidentSpecifics[0]?.id,
              officerId: user?.id,
              primaryOfficer: true,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        updateCallTeam({
          variables: {
            input: {
              incidentId: incidentPayload?.id,
              originatorId: user?.id,
              teamId: user?.team?.id,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        setStatus({
          variables: {
              input: {
                originatorId: user?.id,
                officerId: user?.id,
                officerIncidentSpecificId:
                  incidentPayload?.officerIncidentSpecifics[0]?.id,
                logCodeId: logCodeId,
                statusCode: "23",
              },
            },
          context: { serializationKey: "incidentQueue" },
        }).then(res => {
          const resTenCode =
            res?.data?.setStatus?.officer?.deptAttribute?.status;
          const officerId = res?.data?.setStatus?.officer?.id;
          if (officerId != null && resTenCode != null) {
            availableOfficerDispatch({
              type: UPDATE_AVAILABLE_OFFICER,
              payload: {
                officerId: officerId,
                property: "status",
                value: resTenCode,
              },
            });
          }
        });
      }
      activeIncidentDispatch({
        type: ADD_NEW_INCIDENT,
        payload: incidentPayload,
      });
      incidentDispatch({
        type: ADD_OFFLINE_INCIDENT,
        payload: incidentPayload,
      });
    }

    //Dispatch to open a new tab with the incident
    // incidentDispatch({
    //   type: ADD_OFFLINE_INCIDENT,
    //   payload: incidentPayload
    // })

    //Dispatch to add the incident to the active incidents table (this may need to happen first then open the incident)
    // activeIncidentDispatch({ type: ADD_NEW_INCIDENT, payload: activeIncidentPayload })
  };

  const openPreferences = () => setShowPreferences(true);
  const closePreferences = () => setShowPreferences(false);

  const openShowPasswordChange = () => setShowPasswordChange(true);
  const closePasswordChange = () => setShowPasswordChange(false);
  const showCarStop = user?.ori?.enableCarStop;
  const chatEnabled = user?.ori?.moduleOptions?.find(
    mo => mo?.name === "Chat Option",
  );

  const stickyHeader =
    userPreferences?.stickyHeader === "true" ? "sticky" : "static";
  const addTopOffset =
    userPreferences?.stickyHeader === "true" &&
    userPreferences?.stickyTabs === "true";

  //Uncomment this code and the last button if we want to toggle showing the bulletins
  // const [screenSize, setScreenSize] = useState({
  //   width: window.innerWidth,
  //   height: window.innerHeight
  // })

  // useEffect(() => {
  //   function handleResize() {
  //     setScreenSize({
  //       width: window.innerWidth,
  //       height: window.innerHeight
  //     });
  //   }
  //   window.addEventListener("resize", handleResize);
  //   handleResize();
  //   return () => window.removeEventListener("resize", handleResize);
  // }, []);

  const [showVehicleSearch, setShowVehicleSearch] = useState(false);

  return (
    <AppBar
      data-cy="appbar"
      position={stickyHeader}
      id="dashboard-navbar"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: "#303e4f",
        justifyContent: "space-between",
        height: "auto",
        overflowX: "auto",
        padding: "3px 12px",
        zIndex: 3,
        top: addTopOffset ? "48px" : "",
      }}
      ref={dashboardNavBarRef}
    >
      <Box
        flexGrow="0"
        id="left-dashboard-navbar"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          height: "auto",
        }}
      >
        <div id="wifi-container" style={{ paddingRight: "12px" }}>
          <div
            className={
              import.meta.env.VITE_FORCE_ONLINE === "true"
                ? classes.forcedOnline
                : classes.offline
            }
          >
            {!onlineState ? (
              <Tooltip
                title={
                  import.meta.env.VITE_FORCE_ONLINE === "true"
                    ? "Subscription server is offline"
                    : "Currently OFFLINE"
                }
              >
                <WifiOffIcon style={{ fontSize: "2.5vw" }} />
              </Tooltip>
            ) : socketConnected ? (
              <Tooltip title="Currently online">
                <WifiIcon
                  /*fontSize="large"*/ data-cy="online-indicator"
                  style={{ color: "#13c55a", fontSize: "2.5vw" }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Subscription server offline">
                <WifiIcon
                  /*fontSize="large"*/ data-cy="online-indicator"
                  style={{ color: "yellow", fontSize: "2.5vw" }}
                />
              </Tooltip>
            )}
          </div>
        </div>
        <Box display="flex" flexDirection="column">
          <Typography
            variant="h6"
            data-cy="user-badge-name"
            style={{ marginLeft: "8px", color: "white", fontSize: "1vw" }}
          >
            {user?.homeOriAbbreviation}-{user?.badge}: {user?.firstName}&nbsp;
            {user?.lastName}
          </Typography>
          <ThemeProvider theme={darkTheme}>
            <Autocomplete
              onFocus={() => {
                setPausePolling(true);
              }}
              onBlur={() => {
                setPausePolling(false);
              }}
              blurOnSelect={false}
              multiple
              id="tags-outlined"
              limitTags={3}
              style={{ maxWidth: "30vw" }}
              options={oriList.filter(ori => ori?.abbreviation)}
              onChange={(e, value) => handleChange(e, value)}
              disableClearable
              getOptionLabel={option =>
                option?.oriName + " - " + option?.abbreviation
              }
              value={selectedOriOptions}
              filterSelectedOptions
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      variant="default"
                      label={option.abbreviation}
                      {...tagProps}
                    />
                  );
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  data-cy="selected-oris-select"
                  inputProps={{
                    ...params.inputProps,
                    "data-cy": "selected-oris-input",
                  }}
                  variant="standard"
                />
              )}
            />
            <FormHelperText>Selected ORIs</FormHelperText>
          </ThemeProvider>
        </Box>
        <DutyToggle
          onlineState={onlineState}
          user={user}
          handleDutyChange={handleDutyChange}
        />
      </Box>

      <Box
        flexGrow="1"
        id="button-wrapper"
        justifyContent="space-evenly"
        alignContent={"center"}
        display="flex"
        flexDirection="row"
        style={{ flexWrap: "wrap" }}
      >
        <Box
          flexGrow="1"
          id="incident-buttons-dashboard-navbar"
          justifyContent="space-evenly"
          alignItems={"center"}
          display="flex"
          flexDirection="row"
        >
          <Button
            style={{ fontSize: "1.25vw", height: "3.5vw" }}
            name="new-incident"
            aria-label="new incident button"
            onClick={() => newIncident(false)}
            className={clsx(classes.buttonHover, classes.navBarButtons)}
            data-cy="new-incident-button"
            variant="contained"
          >
            New Incident
          </Button>
          {showCarStop && (
            <Button
              style={{ fontSize: "1.25vw", height: "3.5vw" }}
              name="car-stop"
              aria-label="car stop button"
              data-cy="car-stop-button"
              variant="contained"
              onClick={() => newIncident(true)}
              className={clsx(classes.buttonHover, classes.navBarButtons)}
            >
              Car Stop
            </Button>
          )}
        </Box>
        <Box
          flexGrow="1"
          id="icon-buttons-dashboard-navbar"
          justifyContent="space-between"
          display="flex"
          flexDirection="row"
          alignItems="center"
          flexWrap={"wrap"}
          style={{ padding: "10px 0" }}
        >
          {checkRoles(user?.roleNames, "add_query_returns") && (
            <>
              <Tooltip title="27 Query" placement="top">
                <IconButton
                  size="small"
                  onClick={() => {
                    setQueryType({
                      id: 2,
                      name: "SNQ - 27 Name/DOB",
                      shortcode: "SNQ",
                    });
                    setShowQueryModal(true);
                  }}
                  className={classes.whiteHover}
                >
                  <PersonIcon style={{ fontSize: "2.5vw" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="28 Query" placement="top">
                <IconButton
                  size="small"
                  onClick={() => {
                    setQueryType({
                      id: 1,
                      name: "SVQ - 28",
                      shortcode: "SVQ",
                    });
                    setShowQueryModal(true);
                  }}
                  className={classes.whiteHover}
                >
                  <DirectionsCarIcon style={{ fontSize: "3vw" }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Radio Log" placement="top">
            <IconButton
              size="small"
              onClick={() => {
                setShowRadioModal(true);
              }}
              className={classes.whiteHover}
            >
              <GiWalkieTalkie style={{ fontSize: "2.5vw" }} />
            </IconButton>
          </Tooltip>
          {chatEnabled && (
            <Tooltip title="Chat" placement="top">
              <IconButton
                size="small"
                onClick={openChat}
                className={classes.whiteHover}
              >
                <AiFillMessage style={{ fontSize: "2.5vw" }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Switch to Core" placement="top">
            <IconButton
              size="small"
              onClick={openCore}
              className={classes.whiteHover}
              disabled={!onlineState}
            >
              {/* <AiFillMessage style={{ fontSize: "2.5vw" }} /> */}
              <MdSwapCalls style={{ rotate: "90%", fontSize: "2.5vw" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Night Mode" placement="top">
            <IconButton
              size="small"
              onClick={toggleNightMode}
              className={classes.whiteHover}
            >
              <Brightness4 style={{ fontSize: "2.5vw" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Training Dashboard" placement="top">
            <IconButton
              size="small"
              onClick={handleOpenTrainingDashboard}
              className={classes.whiteHover}
            >
              {/* <AssignmentTurnedInIcon style={{ fontSize: "2.5vw" }} /> */}
              <FaGraduationCap style={{ fontSize: "2.5vw" }} />
            </IconButton>
          </Tooltip>
          {showPortalLink && (
            <Tooltip title="Public Portal" placement="top">
              <IconButton
                size="small"
                onClick={handleOpenPortal}
                className={classes.whiteHover}
              >
                <MdPublic style={{ fontSize: "2.5vw" }} />
              </IconButton>
            </Tooltip>
          )}
          <NewsModal
            userId={user?.id}
            nightMode={nightMode}
            offline={!onlineState}
          />
          <Tooltip title="Map" placement="top">
            <MenuItem
              component={Link}
              target="_blank"
              rel="noreferrer"
              to={`/mp/dashboard-map/${user?.id}/${
                user?.homeOriId
              }/${parseObjectArrayIdString(user?.selectedOrisObjs)}`}
              style={{ padding: "12px" }}
            >
              <Room
                style={{ fontSize: "2.5vw" }}
                className={classes.whiteHover}
              />
            </MenuItem>
          </Tooltip>
          {/* {screenSize?.width < 450 && <Tooltip title="Show Bulletin Drawer" placement="top">
            <IconButton
              size="small"
              onClick={toggleBulletinDrawer}
              className={classes.whiteHover}
            >
              <Brightness4 style={{ fontSize: "2.5vw" }} />
            </IconButton>
          </Tooltip>} */}
          <HamburgerMenu
            openPreferences={openPreferences}
            openShowPasswordChange={openShowPasswordChange}
            displayLogOut={true}
            handleIncidentOpening={handleIncidentOpening}
            handlePersonOpening={handlePersonOpening}
            handleTicketOpening={handleTicketOpening}
            handleVehicleOpening={handleVehicleOpening}
            incidentId={null}
            incidentOriId={null}
            tab={tab}
            setTab={setTab}
            userPreferences={userPreferences}
            setUserPreferences={setUserPreferences}
            showVehicleSearch={showVehicleSearch}
            setShowVehicleSearch={setShowVehicleSearch}
            subscriptionServer={subscriptionServer}
            setPausePolling={setPausePolling}
          />
        </Box>
      </Box>
      <Preferences
        showPreferences={showPreferences}
        closePreferences={closePreferences}
        user={user}
        userId={user?.id}
      />
      {showPasswordChange && (
        <ChangePassword
          showPasswordChange={showPasswordChange}
          closePasswordChange={closePasswordChange}
        />
      )}
      <DmvQueryModal
        setTabIndex={setTab}
        modalOpen={showQueryModal}
        setModalOpen={setShowQueryModal}
        setPopper={setPopper}
        incidentId={null}
        defaultQuery={queryType}
      />
      {showRadioModal && (
        <RadioLogModal
          modalOpen={showRadioModal}
          setModalOpen={setShowRadioModal}
          incidentId={null}
          incidents={radioLogIncidents}
        />
      )}
    </AppBar>
  );
};
const DutyToggle = memo(function DutyToggle({ onlineState, user, handleDutyChange }) {
  const WhiteOnGreyToolTip = withStyles({
    tooltip: {
      color: "white",
      fontSize: "12px",
    },
  })(Tooltip);
  return (
    <ThemeProvider theme={darkTheme}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          marginTop: "auto",
          marginLeft: "2vw",
        }}
      >
        <WhiteOnGreyToolTip
          title={
            onlineState
              ? ""
              : "You are currently offline. Please wait until you reconnect"
          }
        >
          <FormControl variant="standard">
            <Select
              data-cy={"on-duty-select"}
              labelId={"on-duty-label"}
              id={"on-duty-id"}
              value={user?.onDuty || false}
              native={false}
              onChange={handleDutyChange}
              disabled={!onlineState}
            >
              <MenuItem
                data-cy={"on-duty-dropdown-item-true"}
                key={"on-duty-true"}
                value={true}
              >
                True
              </MenuItem>
              <MenuItem
                data-cy={"on-duty-dropdown-item-false"}
                key={"off-duty-false"}
                value={false}
              >
                False
              </MenuItem>
            </Select>
            <FormHelperText>On Duty</FormHelperText>
            {/* {!onlineState && <FormHelperText className={classes.redLabel}>You are currently offline. Please wait until you reconnect. </FormHelperText>} */}
          </FormControl>
        </WhiteOnGreyToolTip>
      </div>
    </ThemeProvider>
  );
});

export default DashboardNavBar;
